import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Users, Star, TrendingUp, Target, ArrowRight, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const PIVOT_NETWORK_URL = "https://pivot2powernetwork.com/";

const stats = [
  { value: "94%", label: "Report Improved Clarity" },
  { value: "89%", label: "Career Advancement" },
  { value: "91%", label: "Income Growth" },
];

const benefits = [
  { icon: Target, title: "Power Guide AI", description: "Personalized strategic recommendations tailored to your journey" },
  { icon: Users, title: "Power Circles", description: "Curated mastermind groups with elite professionals" },
  { icon: TrendingUp, title: "Premium Academy", description: "Exclusive courses and frameworks for career transformation" },
  { icon: Star, title: "Lifestyle Designer", description: "Strategic tools to design your ideal income and life" },
];

export default function CommunitySection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      ref={ref}
      id="community"
      className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="community-section"
    >
      {/* Background */}
      <div className="absolute inset-0 hero-gradient opacity-30" />
      <div className="absolute right-0 top-1/4 w-[500px] h-[500px] gradient-blob" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-['Orbitron'] uppercase tracking-[0.2em] text-primary mb-4 block">
            Exclusive Private Network
          </span>
          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-6">
            Pivot To Power
            <br />
            <span className="gradient-text">Network</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
            Achieve Clarity & Power Through Exclusive Masterminds & Strategic Content.
            Join the private community where professionals gain strategic direction to build power in the AI economy.
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
        >
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className="neon-card p-6 text-center"
            >
              <div className="font-['Orbitron'] text-4xl font-bold text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-muted-foreground uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16"
        >
          {benefits.map((benefit, index) => (
            <div
              key={benefit.title}
              className="bento-card p-6 lg:p-8 flex gap-4"
            >
              <div className="flex-shrink-0">
                <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <benefit.icon className="w-6 h-6 text-primary" />
                </div>
              </div>
              <div>
                <h3 className="font-['Orbitron'] text-lg font-bold uppercase text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Testimonial */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="neon-card p-8 lg:p-12 mb-12"
        >
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-primary fill-primary" />
              ))}
            </div>
            <p className="text-lg sm:text-xl text-foreground italic mb-6">
              "I've never experienced anything like Pivot To Power Network. The clarity I gained transformed my entire approach to the AI economy. I finally know my path forward with absolute certainty."
            </p>
            <p className="text-primary font-semibold">Marcus Johnson</p>
            <p className="text-sm text-muted-foreground">Tech Entrepreneur, Atlanta</p>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center"
        >
          <p className="text-muted-foreground mb-6">
            The future belongs to those who move with clarity. Join now.
          </p>
          <a
            href={PIVOT_NETWORK_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              className="bg-primary text-primary-foreground rounded-full px-10 h-16 font-bold uppercase tracking-wider text-lg hover:bg-primary/90 transition-all btn-glow group"
              data-testid="community-join-btn"
            >
              <Users size={22} className="mr-3" />
              Join Pivot To Power Network
              <ArrowRight size={20} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
